import { IWizardStepProps, SubwayNavNodeState } from '@coherence-design-system/controls';
import * as React from 'react';

import { IMachineLearningExperiment } from '../../../../Common/MachineLearningModels/IMachineLearningExperiment';
import { MachineLearningExperimentDataWizardStep } from './MachineLearningExperimentDataWizardStep';
import { MachineLearningExperimentInfoWizardStep } from './MachineLearningExperimentInfoWizardStep';
import { MachineLearningExperimentModelWizardStep } from './MachineLearningExperimentModelWizardStep';
import { MachineLearningExperimentReviewWizardStep } from './MachineLearningExperimentReviewWizardStep';
import { MachineLearningExperimentWizardStep } from './MachineLearningExperimentWizard';
import { MachineLearningExperimentWizardFooter } from './MachineLearningExperimentWizardFooter';

export type MachineLearningExperimentWizardContextProps = {
  steps: IWizardStepProps[];
  setSteps: React.Dispatch<React.SetStateAction<IWizardStepProps[]>>;
  completedSteps: MachineLearningExperimentWizardStep[];
  setCompletedSteps: React.Dispatch<React.SetStateAction<MachineLearningExperimentWizardStep[]>>;
  experiment: IMachineLearningExperiment;
  setExperiment: React.Dispatch<React.SetStateAction<IMachineLearningExperiment>>;
  isOpen: boolean;
  dismiss(): void;
};

export const MachineLearningExperimentWizardContext = React.createContext<MachineLearningExperimentWizardContextProps>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  {}
);

const initialState: IMachineLearningExperiment = {
  id: null,
  type: null,
  dataOwnerId: null,
  baseEntity: null,
  baseEntityColumns: null,
  joinedEntities: {},
  description: null,
  splitColumn: null,
  disabled: false,
  hiddenProperties: [],
  name: '',
  modelConfiguration: {
    unsupervisedModel: 'IsolationForest',
    unsupervisedModelConfiguration: {
      n_estimators: 100,
      max_samples: 'auto',
      contamination: 0.1,
      random_state: 42,
    },
    supervisedModel: 'RandomForest',
    supervisedModelConfiguration: {
      n_estimators: 100,
      max_samples: 'auto',
      random_state: 42,
    },
    supervisedModelUri: '',
  },
};

export function MachineLearningExperimentWizardContextProvider(props: {
  isOpen: boolean;
  dismiss: () => void;
  children(steps: IWizardStepProps[]): React.ReactElement;
}): React.ReactElement {
  const initialSteps: IWizardStepProps[] = React.useMemo(
    () => [
      {
        id: MachineLearningExperimentWizardStep.INFO,
        footerElement: <MachineLearningExperimentWizardFooter />,
        label: 'General information',
        state: SubwayNavNodeState.NotStarted,
        wizardContent: {
          content: <MachineLearningExperimentInfoWizardStep />,
        },
      },
      {
        id: MachineLearningExperimentWizardStep.MODEL,
        label: 'Model configuration',
        state: SubwayNavNodeState.NotStarted,
        footerElement: <MachineLearningExperimentWizardFooter />,
        wizardContent: {
          content: <MachineLearningExperimentModelWizardStep />,
        },
      },
      {
        id: MachineLearningExperimentWizardStep.DATA,
        label: 'Data configuration',
        state: SubwayNavNodeState.NotStarted,
        footerElement: <MachineLearningExperimentWizardFooter />,
        wizardContent: {
          content: <MachineLearningExperimentDataWizardStep />,
        },
      },
      {
        id: MachineLearningExperimentWizardStep.REVIEW,
        label: 'Review',
        state: SubwayNavNodeState.NotStarted,
        footerElement: <MachineLearningExperimentWizardFooter />,
        wizardContent: {
          content: <MachineLearningExperimentReviewWizardStep />,
        },
      },
    ],
    []
  );

  const [steps, setSteps] = React.useState<IWizardStepProps[]>(initialSteps);
  const [completedSteps, setCompletedSteps] = React.useState<MachineLearningExperimentWizardStep[]>([]);
  const [machineLearningExperiment, setMachineLearningExperiment] =
    React.useState<IMachineLearningExperiment>(initialState);

  React.useEffect(() => {
    if (props.isOpen == true) {
      setMachineLearningExperiment(initialState);
      setSteps(initialSteps);
    }
  }, [initialSteps, props.isOpen]);

  return (
    <MachineLearningExperimentWizardContext.Provider
      value={{
        isOpen: props.isOpen,
        dismiss: props.dismiss,
        steps,
        setSteps,
        completedSteps,
        setCompletedSteps,
        experiment: machineLearningExperiment,
        setExperiment: setMachineLearningExperiment,
      }}
    >
      {props.children(steps)}
    </MachineLearningExperimentWizardContext.Provider>
  );
}
